import { format } from 'date-fns';

import { average } from '../../../utils/helpers/numbers';
import { WIDGET_TYPES } from '../constants';
import { WidgetGrouping } from '../types';
import { DATA_GROUPING_TYPE, DATA_RANGE_TYPE } from '../Widget/constants';

export const transformCSVData = (columns: string[], data: any) => {
  const exportData = [['COMMUNITY', ...columns]];

  Object.values(data).forEach((value: any) => {
    const communityQuarterValues = columns.map((columnName: string) =>
      value[columnName] ? average(value[columnName]).toFixed(2) : ''
    );
    exportData.push([value.communityName, ...communityQuarterValues]);
  });

  return exportData;
};

export const getFilename = (communityName: string, widgetName: string) => {
  const adjustedCommunityName = communityName?.split(' ').join('_');
  const adjustedWidgetyName = widgetName?.split(' ').join('_');

  return `${adjustedCommunityName}_${adjustedWidgetyName}_${format(new Date(), 'yyyyMMdd')}`;
};

export const getGroupByPayload = (groupBy?: WidgetGrouping) => {
  if (!groupBy) return undefined;

  if (groupBy.type === DATA_GROUPING_TYPE.ROOM_TYPE) {
    return { type: groupBy.type, categories: [] };
  }

  return groupBy;
};

export const getDefaultGroupByForWidgetType = (type: number): WidgetGrouping => {
  if (
    [
      WIDGET_TYPES.MARKET_RATE_GRAPH,
      WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH,
      WIDGET_TYPES.MARKET_RATE_AVERAGE_CARE_COST_GRAPH,
      WIDGET_TYPES.ENTRANCE_FEE_GRAPH,
      WIDGET_TYPES.SQUARE_FOOTAGE_GRAPH,
    ].includes(type)
  ) {
    return { type: DATA_GROUPING_TYPE.ROOM_TYPE };
  }

  if (type === WIDGET_TYPES.RATING_GRAPH || type === WIDGET_TYPES.REVIEWS_SUMMARY) {
    return { type: DATA_GROUPING_TYPE.REVIEW_SOURCE };
  }

  return { type: DATA_GROUPING_TYPE.CARE_TYPE };
};

const parseSqFtStart = (rangeString: string) => {
  if (!rangeString) return Number.MAX_SAFE_INTEGER;
  const match = rangeString.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

const sortByApartmentTypes = (data: any, apartmentTypeGroups: any, groupBy: any) => {
  if (!Array.isArray(data)) return data;

  if (groupBy?.type === DATA_GROUPING_TYPE.SQ_FT) {
    return [...data].sort((a, b) => {
      const aStart = parseSqFtStart(a.group_by);
      const bStart = parseSqFtStart(b.group_by);
      return aStart - bStart;
    });
  }

  const groupOrderMap = (apartmentTypeGroups ?? []).reduce((acc: any, group: any, index: number) => {
    acc[group.name] = index;
    return acc;
  }, {});

  return [...data].sort((a, b) => {
    const orderA = groupOrderMap[a.group_by] ?? Number.MAX_SAFE_INTEGER;
    const orderB = groupOrderMap[b.group_by] ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

export const getOrderedWidgetData = (
  data: any,
  rangeType: string,
  type: number,
  apartmentTypeGroups: any,
  groupBy: any
) => {
  if (
    rangeType === DATA_RANGE_TYPE.GROUPED &&
    [
      WIDGET_TYPES.MARKET_RATE_GRAPH,
      WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH,
      WIDGET_TYPES.MARKET_RATE_AVERAGE_CARE_COST_GRAPH,
      WIDGET_TYPES.ENTRANCE_FEE_GRAPH,
      WIDGET_TYPES.SQUARE_FOOTAGE_GRAPH,
    ].includes(type) &&
    Array.isArray(data)
  ) {
    return sortByApartmentTypes(data, apartmentTypeGroups, groupBy);
  }

  return data;
};
